import React, { useEffect, useState } from 'react';
import { Card, Badge, Button, Spinner, Alert, Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faEnvelope,
  faHome,
  faDollarSign,
  faChartLine,
  faRefresh,
  faClock,
  faCheckCircle,
  faBolt,
  faLightbulb,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useLeadAIContext } from '../../hooks/useLeadAIContext';

interface EnhancedLeadContextProps {
  leadId: string;
  className?: string;
  onTasksGenerated?: (tasks: any[]) => void;
}

const EnhancedLeadContext: React.FC<EnhancedLeadContextProps> = ({
  leadId,
  className = '',
  onTasksGenerated
}) => {
  const { context, loading, error, refreshContext, needsRefresh, fetchHaikuTasks } = useLeadAIContext(leadId);
  const [generatingTasks, setGeneratingTasks] = useState(false);
  const [haikuTasks, setHaikuTasks] = useState<any>(null);

  // Auto-refresh if needed
  useEffect(() => {
    if (needsRefresh()) {
      refreshContext();
    }
  }, [needsRefresh, refreshContext]);

  const generateHaikuTasks = async () => {
    setGeneratingTasks(true);
    try {
      const tasks = await fetchHaikuTasks();
      if (tasks) {
        setHaikuTasks(tasks);
        if (onTasksGenerated) {
          onTasksGenerated(tasks.next_steps);
        }
      }
    } catch (err) {
      console.error('Error generating tasks:', err);
    } finally {
      setGeneratingTasks(false);
    }
  };

  if (loading) {
    return (
      <Card className={`${className} text-center p-4`}>
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 mb-0">Loading AI Context...</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`${className}`}>
        <Card.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            Unable to load AI context
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!context) {
    return null;
  }

  const isVIP = context.vip_status?.is_vip;
  const hasPropertyHistory = context.property_history && context.property_history.properties_transacted && context.property_history.properties_transacted.length > 0;

  return (
    <Card className={`${className} enhanced-lead-context`}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
          Enhanced AI Context
        </h5>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={refreshContext}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faRefresh} spin={loading} />
        </Button>
      </Card.Header>
      
      <Card.Body>
        {/* VIP Status */}
        {isVIP && (
          <Alert variant="warning" className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faStar} className="me-2" size="lg" />
            <div>
              <strong>VIP Customer</strong>
              <div className="small">
                {context.vip_status?.transaction_count} transactions worth {context.vip_status?.total_value}
              </div>
            </div>
          </Alert>
        )}

        {/* Property History */}
        {hasPropertyHistory && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">
              <FontAwesomeIcon icon={faHome} className="me-2" />
              Property History
            </h6>
            {context.property_history?.properties_transacted?.map((property, index) => (
              <div key={index} className="mb-2 p-2 bg-light rounded">
                <div className="fw-bold">{property.address}</div>
                <div className="small text-muted">
                  {property.transactions.join(' → ')} | {property.investment_type}
                </div>
              </div>
            ))}
            {context.property_history?.investor_profile && (
              <Badge bg="success" className="mt-2">
                {context.property_history.investor_profile.replace(/_/g, ' ').toUpperCase()}
              </Badge>
            )}
          </div>
        )}

        {/* Email Engagement */}
        {context.behavioral_insights && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Email Insights
            </h6>
            <Row>
              <Col xs={6}>
                <div className="text-center p-2 bg-light rounded">
                  <div className="h4 mb-0 text-primary">
                    {context.total_emails_analyzed || 0}
                  </div>
                  <small>Emails Analyzed</small>
                </div>
              </Col>
              <Col xs={6}>
                <div className="text-center p-2 bg-light rounded">
                  <div className="h4 mb-0 text-success">
                    {Math.round(context.confidence_score || 0)}%
                  </div>
                  <small>Confidence</small>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Buyer Preferences */}
        {context.buyer_context?.preferences && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">
              <FontAwesomeIcon icon={faChartLine} className="me-2" />
              Buyer Preferences
            </h6>
            <div className="small">
              <div><strong>Stage:</strong> {context.buyer_context.search_stage}</div>
              <div><strong>Urgency:</strong> {context.buyer_context.urgency}</div>
            </div>
          </div>
        )}

        {/* AI Task Generation */}
        <div className="mt-3 pt-3 border-top">
          <Button
            variant="primary"
            size="sm"
            className="w-100"
            onClick={generateHaikuTasks}
            disabled={generatingTasks}
          >
            {generatingTasks ? (
              <>
                <Spinner size="sm" className="me-2" />
                Generating Tasks...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                Generate AI Tasks (Haiku)
              </>
            )}
          </Button>
        </div>

        {/* Generated Tasks */}
        {haikuTasks && (
          <div className="mt-3">
            <h6 className="text-muted mb-2">AI-Generated Next Steps</h6>
            <div className="list-group list-group-flush">
              {haikuTasks.next_steps.map((task: any, index: number) => (
                <div key={index} className="list-group-item px-0">
                  <div className="d-flex justify-content-between">
                    <span className="text-truncate">{task.action}</span>
                    <Badge bg={task.priority >= 3 ? 'danger' : 'primary'} className="ms-2">
                      {task.deadline}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            {haikuTasks.urgency && (
              <Alert 
                variant={haikuTasks.urgency === 'critical' ? 'danger' : haikuTasks.urgency === 'high' ? 'warning' : 'info'} 
                className="mt-2 mb-0"
              >
                <small>Urgency: {haikuTasks.urgency.toUpperCase()}</small>
              </Alert>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default EnhancedLeadContext;